// Public Member Profile — /u/:userId
import { useEffect, useState, useRef } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import LivePreview from "../components/LivePreview";
import { useAuth } from "../lib/auth";
import { Heart, Send, Tv, Film, ShoppingBag, MessageCircle, UserPlus, UserCheck, Sparkles, Share2, Edit3, Image as ImageIcon, Link2, X, Camera, Upload, Smile, Video, Play, Crown, Flag, Users, Ban } from "lucide-react";
import { toast, Toaster } from "sonner";
import SponsorBanner from "../components/SponsorBanner";
import ReelPopupAd from "../components/ReelPopupAd";
import ReportModal from "../components/ReportModal";
import ComingSoon from "../components/ComingSoon";
// BoostButton removed in Round J — creators now earn 45% of ad revenue automatically via Business Suite.
import ReferralCard from "../components/ReferralCard";
import PostInteractionsBar from "../components/PostInteractionsBar";
import StoriesUploader from "../components/StoriesUploader";
import StoriesRail from "../components/StoriesRail";
import ReelViewer from "../components/ReelViewer";
import SocialButtons from "../components/SocialButtons";
import UserSearchBar from "../components/UserSearchBar";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";

const EMOJIS = ["🔥","💜","💎","⚡","🎮","🏆","🎯","✨","💀","🚀","👑","🌌","🥇","💯","🎉","😂","😍","🤘","🎵","🎬","🎨","💸","🌈","⭐","🦄","🍕","☕","🎤","💪","🙌"];

const TABS = [
  { id: "feed",    label: "Newsfeed",   icon: MessageCircle },
  { id: "photos",  label: "Photos",     icon: ImageIcon },
  { id: "streams", label: "Streams",    icon: Tv },
  { id: "groups",  label: "Groups",     icon: Users },
  { id: "breels",  label: "BReels",     icon: Film },
  { id: "merch",   label: "Merch",      icon: ShoppingBag },
];

export default function Profile() {
  const { userId } = useParams();
  const { user, refresh } = useAuth();
  const [p, setP] = useState(null);
  const [tab, setTab] = useState("feed");
  const [merch, setMerch] = useState([]);
  const [donateOpen, setDonateOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [storiesOpen, setStoriesOpen] = useState(false);
  const [donateAmt, setDonateAmt] = useState(25);
  const [donateMsg, setDonateMsg] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ display_name: "", bio: "", avatar: "", logo: "", banner_url: "", banner_position: "50% 50%" });
  const [postKind, setPostKind] = useState("text");
  const [postContent, setPostContent] = useState("");
  const [postLink, setPostLink] = useState("");
  // Multi-image support -- previously capped at exactly one image with no way to
  // pick more, even though the profile feed already renders a multi-image grid
  // for any post that has one (see the `images` array logic in the feed render
  // below). postImages/postImageFiles are now arrays; postImage/postImageFile
  // (singular) never existed as a real multi-select path, just a single slot.
  const [postImages, setPostImages] = useState([]);       // data-URL previews
  const [postImageFiles, setPostImageFiles] = useState([]); // matching File objects
  const [postVideo, setPostVideo] = useState(null);
  const [postVideoFile, setPostVideoFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [postBusy, setPostBusy] = useState(false);
  const [postPct, setPostPct] = useState(0);
  // post() previously had no guard at all against rapid repeat clicks --
  // not even a disabled button -- making this the most exposed of the three
  // reel/post upload entry points to the same triple-submit bug fixed elsewhere.
  const postSubmittingRef = useRef(false);
  const [adReelId, setAdReelId] = useState(null);
  const fileRef = useRef(null);
  const videoFileRef = useRef(null);
  const avatarFileRef = useRef(null);
  const logoFileRef = useRef(null);
  const bannerFileRef = useRef(null);
  const bannerBoxRef = useRef(null);
  const [bannerBusy, setBannerBusy] = useState(false);
  const [bannerDragging, setBannerDragging] = useState(false);
  const [search] = useSearchParams();
  // Newsfeed activity rails: reels + currently-live streams scoped to me + friends + follows.
  const [rails, setRails] = useState({ reels: [], live_streams: [] });
  // Same authors as the circular <StoriesRail /> tiles, but with each one's latest
  // story's own media — used to render the rectangle content-preview cards below it.
  const [storyPreviews, setStoryPreviews] = useState([]);
  // The ReelViewer overlay — opened when a rail thumbnail is tapped.
  const [openReel, setOpenReel] = useState(null);

  const load = () => {
    api.get(`/u/${userId}`).then(({ data }) => setP(data));
    api.get("/merch").then(({ data }) => setMerch(data));
    // Rails are scoped to the LOGGED-IN viewer (not the profile owner) — they
    // show my own + friends + followed activity, per the FB-mobile spec.
    if (user) api.get("/feed/rails").then(({ data }) => setRails(data)).catch(() => {});
    api.get("/stories").then(({ data }) => setStoryPreviews(data.items || [])).catch(() => {});
  };
  useEffect(() => { load(); }, [userId, user?.id]);
  useEffect(() => {
    // Keep the rectangle previews in sync with the circles (both read the same
    // /api/stories endpoint) whenever a story is added anywhere on the site.
    const onStoriesRefresh = () => api.get("/stories").then(({ data }) => setStoryPreviews(data.items || [])).catch(() => {});
    window.addEventListener("nexus:stories-refresh", onStoriesRefresh);
    return () => window.removeEventListener("nexus:stories-refresh", onStoriesRefresh);
  }, []);
  useEffect(() => { if (search.get("donate") === "1") setDonateOpen(true); }, [search]);

  if (!p) return <div className="p-20 text-center text-white/40">Summoning profile…</div>;
  const isMe = user?.id === p.id;

  const copyTipLink = () => {
    navigator.clipboard?.writeText(`${window.location.origin}/t/${encodeURIComponent(p.display_name)}`);
    toast.success("Tip link copied — share it anywhere");
  };

  const toggleFollow = async () => {
    if (!user) { toast.error("Sign in to follow"); return; }
    if (p.am_following) await api.delete(`/follow/${p.id}`); else await api.post(`/follow/${p.id}`);
    load();
  };

  const donate = async () => {
    if (!user) { toast.error("Sign in to donate"); return; }
    try {
      await api.post("/donate", { user_id: p.id, amount: parseFloat(donateAmt), message: donateMsg || null });
      toast.success(`${donateAmt} NX donated to ${p.display_name}`);
      setDonateOpen(false); setDonateMsg(""); refresh(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Donation failed"); }
  };

  const readFileAsDataUrl = (file) => new Promise((res, rej) => {
    const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
  });

  const pickImage = async (e, set) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 4_000_000) { toast.error("Max 4MB"); return; }
    set(await readFileAsDataUrl(f));
  };

  // Post-composer image/video: keep the base64 preview for the inline <img>/<video>
  // shown before submitting, but also hold the raw File — at submit time we upload
  // it for real via /upload/image|video instead of sending the base64 to the server.
  // Sending the base64 as media_b64 used to get copied straight into media_url,
  // which every feed/profile endpoint reads unprojected, bloating every page load.
  const MAX_POST_IMAGES = 10;
  const pickPostImage = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-picking the same file(s) again after a remove
    if (!picked.length) return;
    const room = MAX_POST_IMAGES - postImageFiles.length;
    if (room <= 0) { toast.error(`Max ${MAX_POST_IMAGES} images per post`); return; }
    const oversized = picked.some((f) => f.size > 4_000_000);
    if (oversized) toast.error("Some images over 4MB were skipped");
    const accepted = picked.filter((f) => f.size <= 4_000_000).slice(0, room);
    if (picked.length > room) toast.error(`Only added ${room} more — max ${MAX_POST_IMAGES} per post`);
    const previews = await Promise.all(accepted.map(readFileAsDataUrl));
    setPostImageFiles((cur) => [...cur, ...accepted]);
    setPostImages((cur) => [...cur, ...previews]);
  };
  const removePostImage = (idx) => {
    setPostImageFiles((cur) => cur.filter((_, i) => i !== idx));
    setPostImages((cur) => cur.filter((_, i) => i !== idx));
  };

  const pickVideo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    // Matches the backend's GridFS upload cap (nexus_uploads.py MAX_BYTES).
    if (f.size > 1000 * 1024 * 1024) { toast.error("Max 1000MB video"); return; }
    setPostVideoFile(f);
    // Reels can run large (up to 500MB) -- reading that into a base64 data URL for
    // the preview would blow up browser memory. A blob URL just references the
    // file on disk, so previewing stays cheap regardless of file size.
    if (postVideo?.startsWith("blob:")) URL.revokeObjectURL(postVideo);
    setPostVideo(URL.createObjectURL(f));
  };

  const post = async () => {
    if (!postContent.trim() && postImageFiles.length === 0 && !postVideo && !postLink) { toast.error("Add some content"); return; }
    if (postSubmittingRef.current) return;
    postSubmittingRef.current = true;
    setPostBusy(true);
    setPostPct(0);
    try {
      let media_url;
      let media_urls;
      if (postVideoFile) {
        const fd = new FormData(); fd.append("file", postVideoFile);
        const { data } = await api.post("/upload/video", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => { if (evt.total) setPostPct(Math.round((evt.loaded / evt.total) * 100)); },
        });
        media_url = (process.env.REACT_APP_BACKEND_URL || "") + data.url;
      } else if (postImageFiles.length > 0) {
        // Sequential, not parallel -- so the single progress bar means something
        // (parallel uploads would all report 0-100% independently and the bar
        // would jump around instead of advancing steadily across the batch).
        const urls = [];
        for (let i = 0; i < postImageFiles.length; i++) {
          const fd = new FormData(); fd.append("file", postImageFiles[i]);
          const { data } = await api.post("/upload/image", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (evt) => {
              if (!evt.total) return;
              const perFile = 100 / postImageFiles.length;
              setPostPct(Math.round(i * perFile + (evt.loaded / evt.total) * perFile));
            },
          });
          urls.push((process.env.REACT_APP_BACKEND_URL || "") + data.url);
        }
        media_urls = urls;
        media_url = urls[0];
      }
      await api.post("/posts/v2", {
        content: postContent, kind: postKind,
        media_url, media_urls,
        link_url: postLink || undefined,
      });
      toast.success("Posted");
      if (postVideo?.startsWith("blob:")) URL.revokeObjectURL(postVideo);
      setPostContent(""); setPostImages([]); setPostImageFiles([]); setPostVideo(null); setPostVideoFile(null); setPostLink(""); setPostKind("text"); setEmojiOpen(false); load();
    } catch { toast.error("Post failed"); }
    postSubmittingRef.current = false;
    setPostBusy(false);
  };

  const insertEmoji = (e) => { setPostContent((c) => c + e); };

  const openEdit = () => {
    setEditForm({ display_name: p.display_name || "", bio: p.bio || "", avatar: p.avatar || "", logo: p.logo || "", banner_url: p.banner_url || "", banner_position: p.banner_position || "50% 50%" });
    setEditOpen(true);
  };

  // Real upload (not base64) -- the banner is 1500x500-recommended, base64-inlining
  // it onto the user doc the way avatar/logo do would bloat every /u/{id} response
  // that reads this profile (same reasoning already applied to post/reel media_url).
  const uploadBanner = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 10_000_000) { toast.error("Max 10MB"); return; }
    setBannerBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = (process.env.REACT_APP_BACKEND_URL || "") + data.url;
      setEditForm((cur) => ({ ...cur, banner_url: url }));
      toast.success("Banner uploaded");
    } catch { toast.error("Banner upload failed"); }
    setBannerBusy(false);
  };

  // Drag-to-reposition -- lets a photo that isn't already banner-shaped be
  // repositioned so the important part isn't what object-cover crops off.
  // Pointer events cover both mouse and touch with one handler.
  const bannerPointFromEvent = (e) => {
    const box = bannerBoxRef.current;
    if (!box) return null;
    const rect = box.getBoundingClientRect();
    const x = Math.min(100, Math.max(0, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.min(100, Math.max(0, ((e.clientY - rect.top) / rect.height) * 100));
    return `${x.toFixed(0)}% ${y.toFixed(0)}%`;
  };
  const startBannerDrag = (e) => {
    if (!editForm.banner_url) return;
    e.preventDefault();
    setBannerDragging(true);
    const pos = bannerPointFromEvent(e);
    if (pos) setEditForm((cur) => ({ ...cur, banner_position: pos }));
  };
  const moveBannerDrag = (e) => {
    if (!bannerDragging) return;
    const pos = bannerPointFromEvent(e);
    if (pos) setEditForm((cur) => ({ ...cur, banner_position: pos }));
  };
  const endBannerDrag = () => setBannerDragging(false);

  const saveEdit = async () => {
    try {
      if (editForm.avatar?.startsWith("data:") || editForm.logo?.startsWith("data:")) {
        await api.post("/me/avatar_upload", {
          avatar_b64: editForm.avatar?.startsWith("data:") ? editForm.avatar : undefined,
          logo_b64: editForm.logo?.startsWith("data:") ? editForm.logo : undefined,
        });
      }
      await api.patch("/me/profile", {
        display_name: editForm.display_name || undefined,
        bio: editForm.bio || undefined,
        avatar: editForm.avatar?.startsWith("http") ? editForm.avatar : undefined,
        logo: editForm.logo?.startsWith("http") ? editForm.logo : undefined,
        banner_url: editForm.banner_url?.startsWith("http") ? editForm.banner_url : undefined,
        banner_position: editForm.banner_url ? editForm.banner_position : undefined,
      });
      toast.success("Profile updated"); setEditOpen(false); refresh(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  return (
    <div className="profile-light max-w-5xl mx-auto space-y-8 px-4 pt-4 pb-12" data-testid="profile-page">
      <Toaster theme="dark" position="top-center" />
      <StoriesUploader open={storiesOpen} onClose={() => setStoriesOpen(false)} onPosted={load} />

      {/* Find-people search — visible on every profile so viewers can jump to
          any other member's page (Follow / Friend / Message already wired). */}
      <UserSearchBar variant="page" placeholder={`Find people on Nexus — search by name…`} />

      {/* HEADER — Facebook-width white card */}
      <div className="relative overflow-hidden rounded-2xl border border-black/10 bg-white text-nexus-obsidian shadow-sm">
        <div className="h-32 lg:h-48 bg-gradient-to-r from-nexus-cyan/30 via-nexus-magenta/20 to-nexus-gold/30">
          {p.banner_url && <img src={p.banner_url} alt="" className="w-full h-full object-cover" style={{ objectPosition: p.banner_position || "50% 50%" }} />}
        </div>
        <div className="px-6 lg:px-10 pb-6 -mt-12 lg:-mt-16">
          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
            <button
              onClick={() => isMe && setStoriesOpen(true)}
              className="relative shrink-0 group"
              title={isMe ? "Tap to add a Story" : ""}
              data-testid={isMe ? "avatar-story-trigger" : "avatar"}
            >
              <img src={p.avatar} alt="" className={`w-28 h-28 lg:w-36 lg:h-36 rounded-full ring-4 ring-white object-cover shadow-md ${isMe ? "ring-gradient-to-tr from-nexus-pink to-nexus-gold hover:opacity-90" : ""}`} />
              {isMe && (
                <span className="absolute bottom-1 right-1 bg-nexus-gold rounded-full w-7 h-7 flex items-center justify-center ring-2 ring-white shadow group-hover:scale-110 transition">
                  <Camera className="w-3.5 h-3.5 text-black" />
                </span>
              )}
              {p.logo && <img src={p.logo} alt="" className="absolute -bottom-1 -right-1 w-10 h-10 rounded-full ring-2 ring-white object-cover" />}
            </button>
            <div className="flex-1 min-w-0 sm:self-start sm:mt-16">
              <div className="text-[10px] tracking-[0.3em] uppercase text-nexus-cyan font-display">// {p.role}</div>
              <h1 className="font-display font-black text-2xl lg:text-4xl tracking-tight">{p.display_name}</h1>
              {p.bio && <p className="text-nexus-obsidian/70 mt-2 max-w-2xl text-sm">{p.bio}</p>}
              <div className="flex flex-wrap items-center gap-5 mt-3 text-sm">
                <span><strong>L{p.level.level}</strong><span className="text-nexus-obsidian/50 ml-1">level</span></span>
                <span><strong>{p.followers}</strong><span className="text-nexus-obsidian/50 ml-1">followers</span></span>
                <span><strong>{p.following}</strong><span className="text-nexus-obsidian/50 ml-1">following</span></span>
                {p.wallet_usd_equivalent !== undefined && <span className="text-nexus-amber"><strong>${p.wallet_usd_equivalent}</strong><span className="ml-1 text-nexus-obsidian/50">wallet</span></span>}
              </div>
              <div className="h-1.5 bg-black/5 mt-3 rounded-full max-w-md overflow-hidden">
                <div className="h-full bg-gradient-to-r from-nexus-cyan to-nexus-magenta" style={{ width: `${p.level.pct}%` }} />
              </div>
              <div className="text-[10px] text-nexus-obsidian/40 mt-1 font-mono">{p.level.current_level_xp} / {p.level.next_level_xp} XP → L{p.level.level + 1}</div>
            </div>
          <div className="flex flex-col gap-2 w-full sm:w-auto sm:self-start sm:mt-20">
            {!isMe && (
              <>
                <button onClick={toggleFollow} className="rounded-full px-4 py-2 text-xs font-display tracking-[0.25em] uppercase border border-nexus-obsidian/20 hover:bg-nexus-obsidian hover:text-white transition" data-testid="follow-btn">
                  {p.am_following ? <><UserCheck className="w-3.5 h-3.5 inline mr-1" /> Following</> : <><UserPlus className="w-3.5 h-3.5 inline mr-1" /> Follow</>}
                </button>
                <button onClick={() => setDonateOpen(true)} className="rounded-full px-4 py-2 text-xs font-display tracking-[0.25em] uppercase bg-gradient-to-r from-nexus-gold to-nexus-pink text-black shadow" data-testid="donate-btn"><Sparkles className="w-3.5 h-3.5 inline mr-1" /> Donate Nexus</button>
                <button onClick={copyTipLink} className="rounded-full px-3 py-1.5 text-[10px] text-nexus-obsidian/60 hover:text-nexus-obsidian" data-testid="copy-tip-link"><Share2 className="w-3 h-3 inline mr-1" /> Copy tip link</button>
                <button onClick={() => setReportOpen(true)} className="rounded-full px-3 py-1.5 text-[10px] text-nexus-crimson/70 hover:text-nexus-crimson" data-testid="report-profile-btn"><Flag className="w-3 h-3 inline mr-1" /> Report</button>
                <ProfileBlockButton targetId={p.id} targetName={p.display_name} />
                <SocialButtons otherUserId={p.id} />
              </>
            )}
            {isMe && <button onClick={openEdit} className="btn-ghost" data-testid="edit-profile-btn"><Edit3 className="w-3.5 h-3.5" /> Edit profile</button>}
            {isMe && user?.role === "owner" && (
              <Link to="/owner" className="rounded-full px-4 py-2 text-[10px] font-display tracking-[0.25em] uppercase bg-gradient-to-r from-nexus-gold to-nexus-amber text-black text-center shadow" data-testid="jump-owner-hq">
                <Crown className="w-3.5 h-3.5 inline mr-1.5" /> Owner HQ
              </Link>
            )}
            {isMe && (user?.role === "creator" || user?.role === "owner") && (
              <Link to="/studio" className="rounded-full px-4 py-2 text-[10px] font-display tracking-[0.25em] uppercase bg-gradient-to-r from-nexus-magenta to-nexus-pink text-white text-center shadow" data-testid="jump-studio">
                <Tv className="w-3.5 h-3.5 inline mr-1.5" /> Studio Dashboard
              </Link>
            )}
            {isMe && (
              <Link to="/dm" className="rounded-full px-4 py-2 text-[10px] font-display tracking-[0.25em] uppercase border border-nexus-obsidian/20 text-nexus-obsidian text-center hover:bg-nexus-obsidian hover:text-white transition" data-testid="jump-messenger">
                <MessageCircle className="w-3.5 h-3.5 inline mr-1.5" /> Messenger
              </Link>
            )}
            {isMe && (
              <Link to="/groups?create=1" className="rounded-full px-4 py-2 text-[10px] font-display tracking-[0.25em] uppercase bg-gradient-to-r from-nexus-cyan to-nexus-mint text-black text-center shadow" data-testid="jump-create-group">
                <Users className="w-3.5 h-3.5 inline mr-1.5" /> Create group
              </Link>
            )}
            {isMe && (
              <Link to="/groups" className="rounded-full px-4 py-2 text-[10px] font-display tracking-[0.25em] uppercase border border-nexus-obsidian/20 text-nexus-obsidian text-center hover:bg-nexus-obsidian hover:text-white transition" data-testid="jump-groups">
                <Users className="w-3.5 h-3.5 inline mr-1.5" /> My groups
              </Link>
            )}
            {user?.role === "owner" && !isMe && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button
                    className={`btn-ghost text-[10px] ${p.is_sponsored ? "!border-nexus-crimson !text-nexus-crimson" : "!border-nexus-gold !text-nexus-gold"}`}
                    data-testid={p.is_sponsored ? "demote-sponsored-btn" : "promote-sponsored-btn"}
                  >
                    <Sparkles className="w-3 h-3" /> {p.is_sponsored ? "Demote sponsor" : "Promote to Sponsored"}
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="sponsor-confirm-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>{p.is_sponsored ? "Remove sponsorship" : "Promote to Sponsored"}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {p.is_sponsored
                        ? `${p.display_name} will be removed from the rotating Sponsored Spotlight and lose the Studio perk.`
                        : `${p.display_name} will join the Sponsored Spotlight on the Landing page and unlock free Studio tier.`}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="sponsor-confirm-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      data-testid="sponsor-confirm-go"
                      onClick={async () => {
                        const action = p.is_sponsored ? "demote-sponsored" : "promote-sponsored";
                        const verb = p.is_sponsored ? "Removed sponsorship" : "Promoted to Sponsored";
                        try {
                          await api.post(`/owner/${action}`, { user_id: p.id });
                          toast.success(`${verb} ✓`);
                          load();
                        } catch (e) { toast.error("Action failed"); }
                      }}
                    >
                      {p.is_sponsored ? "Demote" : "Promote ★"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            {p.is_sponsored && (
              <span className="text-[9px] tracking-[0.3em] uppercase font-display px-2 py-1 bg-nexus-gold text-nexus-obsidian rounded-full" data-testid="sponsored-badge">★ Sponsored</span>
            )}
          </div>
        </div>
        </div>
      </div>

      {/* LIVE STREAM EMBED — if this creator is currently live, show it inline (Twitch/FB Gaming style) */}
      {(() => {
        const liveStream = (p.streams || []).find((s) => s.is_live);
        if (!liveStream) return null;
        return (
          <div className="rounded-2xl overflow-hidden border border-black/10 bg-white shadow-sm" data-testid="profile-live-embed">
            <div className="aspect-video bg-black relative">
              {liveStream.livekit_room
                ? <LivePreview streamId={liveStream.id} fallback={null} />
                : <img src={liveStream.thumbnail} alt="" className="w-full h-full object-cover" />}
              <div className="absolute top-3 left-3 bg-nexus-crimson text-white px-2 py-1 text-[10px] tracking-[0.3em] font-display uppercase animate-pulse">● LIVE</div>
              <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                <div className="bg-black/70 text-white px-3 py-1.5 rounded-full text-xs font-medium truncate max-w-[70%]">{liveStream.title}</div>
                <Link to={`/watch/${liveStream.id}`} className="rounded-full bg-nexus-cyan text-black px-4 py-1.5 text-[10px] tracking-[0.25em] uppercase font-display shadow" data-testid="open-fullscreen-watch">Full screen ↗</Link>
              </div>
            </div>
          </div>
        );
      })()}

      {/* TABS */}
      <div className="flex flex-wrap gap-1 border-b border-white/10">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`} className={`px-4 py-2 text-[10px] tracking-[0.24em] uppercase font-display flex items-center gap-2 ${tab === t.id ? "text-nexus-cyan border-b-2 border-nexus-cyan" : "text-white/40 hover:text-white"}`}>
            <t.icon className="w-3.5 h-3.5" />{t.label}
          </button>
        ))}
      </div>

      {isMe && <ReferralCard />}

      {/* COMPULSORY SPONSOR BANNER — every profile earns share */}
      <SponsorBanner surface="boost" creatorId={p.id} hideForOwner={false} />

      {tab === "feed" && (
        <section className="max-w-4xl mx-auto space-y-4" data-testid="feed-tab">
          {isMe && (
            <div className="glass p-5 space-y-3 rounded-2xl" data-testid="post-composer">
              <div className="flex gap-1 flex-wrap">
                {[["text","Text"],["image","Image"],["video","Reel"],["link","Link"]].map(([k,l]) => (
                  <button key={k} onClick={() => setPostKind(k)} className={`text-[10px] tracking-[0.3em] uppercase font-display px-3 py-1.5 ${postKind===k ? "text-nexus-cyan border-b-2 border-nexus-cyan" : "text-white/40"}`} data-testid={`post-kind-${k}`}>{l}</button>
                ))}
              </div>
              <textarea value={postContent} onChange={(e) => setPostContent(e.target.value)} placeholder="What's on your mind?" className="w-full bg-transparent border border-white/10 px-4 py-3 text-base min-h-32 resize-y rounded-xl" data-testid="post-content" />
              <div className="flex items-center gap-2 relative">
                <button onClick={() => setEmojiOpen((o) => !o)} className="btn-ghost text-[10px]" data-testid="emoji-toggle"><Smile className="w-3 h-3" /> Emoji</button>
                {emojiOpen && (
                  <div className="absolute top-full left-0 mt-1 z-20 glass-strong p-2 grid grid-cols-10 gap-1 max-w-xs" data-testid="emoji-picker">
                    {EMOJIS.map((e) => (
                      <button key={e} onClick={() => insertEmoji(e)} className="text-lg hover:scale-125 transition-transform p-1">{e}</button>
                    ))}
                  </div>
                )}
              </div>
              {postKind === "image" && (
                <div className="space-y-2">
                  {postImages.length > 0 && (
                    <div className="flex flex-wrap gap-2" data-testid="post-composer-image-previews">
                      {postImages.map((src, i) => (
                        <div key={i} className="relative">
                          <img src={src} alt="" className="h-24 w-24 object-cover border border-white/10" />
                          <button onClick={() => removePostImage(i)} className="absolute -top-1.5 -right-1.5 bg-black/80 hover:bg-nexus-crimson rounded-full w-5 h-5 flex items-center justify-center" aria-label="Remove image" data-testid={`post-composer-remove-image-${i}`}>
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={pickPostImage} />
                  <button onClick={() => fileRef.current?.click()} className="btn-ghost text-[10px]" data-testid="post-composer-pick-images"><ImageIcon className="w-3 h-3" /> {postImages.length > 0 ? `Add more (${postImages.length}/${MAX_POST_IMAGES})` : "Pick images"}</button>
                </div>
              )}
              {postKind === "video" && (
                <div className="space-y-2">
                  {postVideo && (postVideo.startsWith("data:") || postVideo.startsWith("blob:") ? <video src={postVideo} controls className="max-h-48 border border-white/10" /> : <div className="text-[10px] text-nexus-cyan border border-nexus-cyan/30 p-2 break-all">Uploaded → {postVideo}</div>)}
                  <input ref={videoFileRef} type="file" accept="video/*" className="hidden" onChange={pickVideo} />
                  <button onClick={() => videoFileRef.current?.click()} className="btn-ghost text-[10px]" data-testid="pick-video"><Video className="w-3 h-3" /> {postVideo ? "Change reel" : "Pick reel (max 1000MB)"}</button>
                  {uploadProgress > 0 && uploadProgress < 100 && (
                    <div className="space-y-1" data-testid="upload-progress">
                      <div className="h-1 bg-white/10 overflow-hidden"><div className="h-full bg-nexus-cyan transition-all" style={{ width: `${uploadProgress}%` }} /></div>
                      <div className="text-[10px] text-white/50">Uploading… {uploadProgress}%</div>
                    </div>
                  )}
                  <div className="text-[10px] text-white/40">Reels auto-play with a sponsored banner — earns you ad revenue.</div>
                </div>
              )}
              {postKind === "link" && (
                <input value={postLink} onChange={(e) => setPostLink(e.target.value)} placeholder="https://…" className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm" data-testid="post-link" />
              )}
              <button onClick={post} disabled={postBusy} className="btn-nexus !py-2 !px-4 text-[10px] disabled:opacity-50" data-testid="post-submit"><Send className="w-3 h-3" /> {postBusy ? (postPct < 100 ? `${postPct}%` : "Posting…") : "Post"}</button>
            </div>
          )}
          {/* === Newsfeed Activity Rails (mobile-first) ===
              1. Stories rail — reused StoriesRail component (own + friends + follows)
              2. Reels rail   — horizontal-scrolling reel thumbnails
              3. Live rail    — horizontal-scrolling live-stream thumbnails
              All three scoped to the LOGGED-IN viewer's own graph, not the profile
              owner's, so this section is identical whether you're on your own page
              or someone else's — think of it as "your ambient feed", above the wall.
              Hidden if the viewer isn't logged in (rails are personal). */}
          {user && (
            <div className="-mx-4 sm:mx-0 space-y-4" data-testid="profile-rails">
              {/* Stories — circular tiles first (reuse existing component, has its own
                  scroll + viewer), then a "STORIES" title, then rectangle content-preview
                  cards for the same authors underneath — mirrors the Reels card style so
                  each rail reads as its own distinct section. */}
              <div className="px-4 sm:px-0" data-testid="stories-header">
                <div className="-mx-4 sm:mx-0">
                  <StoriesRail />
                </div>
                {storyPreviews.length > 0 && (
                  <>
                    <div className="flex items-center justify-between mb-2 mt-3">
                      <div className="text-[11px] tracking-[0.3em] uppercase font-display text-white/60 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-nexus-gold" />Stories</div>
                    </div>
                    <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1 snap-x snap-mandatory">
                      {storyPreviews.map((t) => (
                        <button
                          onClick={() => window.dispatchEvent(new CustomEvent("nexus:story-open-request", { detail: t }))}
                          key={`story-preview-${t.user_id}`}
                          className="shrink-0 w-32 aspect-[9/16] rounded-xl overflow-hidden relative bg-black snap-start ring-1 ring-white/10 hover:ring-nexus-gold transition text-left"
                          data-testid={`story-preview-${t.user_id}`}
                        >
                          {t.media_url && (t.media_url.endsWith(".mp4") || t.media_url.endsWith(".webm") || t.media_url.endsWith(".mov"))
                            ? <video src={t.media_url} muted playsInline className="w-full h-full object-cover" />
                            : t.media_url
                              ? <img src={t.media_url} alt="" className="w-full h-full object-cover" />
                              : <div className="w-full h-full flex items-center justify-center text-white text-[10px] p-2 text-center bg-gradient-to-br from-nexus-pink via-nexus-magenta to-nexus-cyan">{t.content || t.user_name}</div>}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                          <div className="absolute top-1.5 left-1.5 w-6 h-6 rounded-full ring-2 ring-white overflow-hidden shrink-0">
                            <img src={t.user_avatar || `https://api.dicebear.com/7.x/identicon/svg?seed=${t.user_id}`} className="w-full h-full object-cover" alt="" />
                          </div>
                          <div className="absolute bottom-2 left-2 right-2 text-[9px] text-white truncate">{t.user_name}</div>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Reels rail */}
              {rails.reels.length > 0 && (
                <div className="px-4 sm:px-0" data-testid="reels-rail">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[11px] tracking-[0.3em] uppercase font-display text-white/60 flex items-center gap-2"><Film className="w-3.5 h-3.5 text-nexus-cyan" />Reels</div>
                    <Link to="/reels" className="text-[10px] text-white/40 hover:text-white transition">See all →</Link>
                  </div>
                  <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1 snap-x snap-mandatory">
                    {rails.reels.map((r) => (
                      <button
                        onClick={() => setOpenReel({ ...r, media_url: r.media_url, kind: (r.source === "breel") ? "video" : "reel" })}
                        key={`reel-${r.id}`}
                        className="shrink-0 w-32 aspect-[9/16] rounded-xl overflow-hidden relative bg-black snap-start ring-1 ring-white/10 hover:ring-nexus-cyan transition text-left"
                        data-testid={`reel-thumb-${r.id}`}
                      >
                        {r.thumbnail && (r.thumbnail.startsWith("data:video") || r.thumbnail.endsWith(".mp4") || r.thumbnail.endsWith(".webm"))
                          ? <video src={r.thumbnail} muted playsInline className="w-full h-full object-cover" />
                          : <img src={r.thumbnail || r.media_url} alt="" className="w-full h-full object-cover" />}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                        <div className="absolute bottom-2 left-2 right-2 text-[9px] text-white truncate">{r.user_name}</div>
                        <Play className="absolute inset-0 m-auto w-6 h-6 text-white/85 drop-shadow-lg" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Live rail — header always shows (matches Reels), with an empty state
                  when nobody's currently live, so the section itself never disappears. */}
              <div className="px-4 sm:px-0" data-testid="live-rail">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] tracking-[0.3em] uppercase font-display text-white/60 flex items-center gap-2"><Tv className="w-3.5 h-3.5 text-nexus-crimson" />Live now</div>
                  {rails.live_streams.length > 0 && <Link to="/live" className="text-[10px] text-white/40 hover:text-white transition">See all →</Link>}
                </div>
                {rails.live_streams.length > 0 ? (
                  <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1 snap-x snap-mandatory">
                    {rails.live_streams.map((s) => (
                      <Link
                        to={`/watch/${s.id}`}
                        key={`live-${s.id}`}
                        className="shrink-0 w-48 aspect-video rounded-xl overflow-hidden relative bg-black snap-start ring-1 ring-white/10 hover:ring-nexus-cyan transition"
                        data-testid={`live-thumb-${s.id}`}
                      >
                        <img src={s.thumbnail} alt="" className="w-full h-full object-cover" />
                        {s.livekit_room && <LivePreview streamId={s.id} fallback={null} />}
                        <div className="absolute top-2 left-2 bg-nexus-crimson text-white text-[8px] font-display tracking-widest uppercase px-1.5 py-0.5 animate-pulse">● LIVE</div>
                        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/85 to-transparent p-2">
                          <div className="text-[10px] text-white font-medium truncate">{s.title}</div>
                          <div className="text-[9px] text-white/70">{s.creator_name} · {(s.viewers || 0).toLocaleString()} watching</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-[10px] text-white/40 py-2" data-testid="live-rail-empty">No one&rsquo;s live right now — check back later.</div>
                )}
              </div>
            </div>
          )}

          {p.posts.length === 0 && <div className="text-white/40 text-base text-center py-12">No posts yet.</div>}
          {p.posts.map((post) => {
            const isReel = post.kind === "video" || post.kind === "reel" || (post.media_url && typeof post.media_url === "string" && post.media_url.startsWith("data:video"));
            const canDeletePost = user && (post.user_id === user.id || user.role === "owner" || user.role === "staff");
            // Multi-image support — server normalises legacy `media_url` into
            // `media_urls`, so treat everything as an array here.
            const images = !isReel ? (post.media_urls && post.media_urls.length
              ? post.media_urls
              : (post.media_url ? [post.media_url] : [])) : [];
            // Facebook-mobile-style multi-image grid layout
            const gridCls = images.length === 1 ? "grid-cols-1"
                          : images.length === 2 ? "grid-cols-2"
                          : images.length === 3 ? "grid-cols-2"           // first big + 2 stacked
                          : "grid-cols-2";                                  // 2×2 with +N badge
            return (
              // Full-width media on mobile: edge-to-edge card by removing the outer
              // horizontal padding on <sm and inset it back on ≥sm. All the text
              // and interactions live inside a padded inner block instead.
              <div key={post.id} className="bg-nexus-surface sm:border sm:border-white/10 sm:rounded-2xl relative overflow-hidden -mx-4 sm:mx-0" data-testid={`post-${post.id}`}>
                <div className="flex items-center gap-3 px-4 pt-4">
                  <Link to={`/u/${post.user_id}`}><img src={post.user_avatar} alt="" className="w-11 h-11 rounded-full ring-1 ring-white/20 hover:ring-nexus-cyan transition" /></Link>
                  <div className="min-w-0">
                    <Link to={`/u/${post.user_id}`} className="text-base font-medium hover:text-nexus-cyan transition block truncate">{post.user_name}</Link>
                    <div className="text-[11px] text-white/40" data-testid={`post-ts-${post.id}`}>{new Date(post.ts).toLocaleString()}</div>
                  </div>
                  {canDeletePost && (
                    <button
                      onClick={async () => {
                        if (!window.confirm("Delete this post? This cannot be undone.")) return;
                        try {
                          await api.delete(`/posts/${post.id}`);
                          setP((cur) => cur ? { ...cur, posts: cur.posts.filter((x) => x.id !== post.id) } : cur);
                          toast.success("Post deleted");
                        } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
                      }}
                      className="ml-auto text-white/40 hover:text-nexus-crimson transition"
                      title="Delete post"
                      aria-label="Delete post"
                      data-testid={`delete-post-${post.id}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {post.content && <div className="text-sm text-white/85 whitespace-pre-wrap px-4 pt-3">{post.content}</div>}
                {isReel ? (
                  <div className="relative mt-3" data-testid={`reel-${post.id}`}>
                    <video
                      src={post.media_url}
                      controls
                      playsInline
                      className="w-full bg-black block"
                      onPlay={() => setAdReelId(post.id)}
                      onPause={() => setAdReelId((cur) => (cur === post.id ? null : cur))}
                      onEnded={() => setAdReelId((cur) => (cur === post.id ? null : cur))}
                    />
                    <ReelPopupAd visible={adReelId === post.id} creatorId={post.user_id} onClose={() => setAdReelId(null)} />
                  </div>
                ) : (
                  images.length > 0 && (
                    <div className={`mt-3 grid gap-0.5 ${gridCls}`} data-testid={`post-media-${post.id}`}>
                      {images.length === 3 ? (
                        <>
                          <img src={images[0]} alt="" className="w-full aspect-square object-cover row-span-2" />
                          <img src={images[1]} alt="" className="w-full aspect-square object-cover" />
                          <img src={images[2]} alt="" className="w-full aspect-square object-cover" />
                        </>
                      ) : images.slice(0, 4).map((u, i) => (
                        <div key={i} className="relative">
                          {/* Single image: object-contain + a background instead of object-cover --
                              object-cover on a fixed max-height box was cropping the top/bottom off
                              any photo that wasn't already landscape-ish (portrait phone photos, the
                              exact complaint). Multi-image grid tiles stay object-cover/aspect-square
                              since those are deliberately uniform crop tiles, same as Instagram/FB. */}
                          <img src={u} alt="" className={images.length === 1 ? "w-full max-h-[600px] object-contain bg-black" : "w-full aspect-square object-cover"} />
                          {i === 3 && images.length > 4 && (
                            <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white text-2xl font-display">+{images.length - 4}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )
                )}
                {post.link_url && <a href={post.link_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 mt-3 text-nexus-cyan text-xs hover:underline"><Link2 className="w-3 h-3" /> {post.link_url}</a>}
                <div className="px-4 pb-4 pt-2">
                  <PostInteractionsBar post={post} onChange={load} />
                </div>
              </div>
            );
          })}
        </section>
      )}

      {tab === "photos" && (() => {
        // Flattens every post's images into one gallery -- previously there was no
        // way to browse a creator's photos except scrolling the whole newsfeed.
        // Reuses the same media_urls-with-legacy-media_url-fallback normalisation
        // as the feed render above.
        const allPhotos = [];
        for (const post of p.posts) {
          const isReelPost = post.kind === "video" || post.kind === "reel";
          if (isReelPost) continue;
          const urls = post.media_urls && post.media_urls.length ? post.media_urls : (post.media_url ? [post.media_url] : []);
          for (const u of urls) allPhotos.push({ url: u, postId: post.id });
        }
        return (
          <section data-testid="photos-tab">
            {allPhotos.length === 0 ? (
              <div className="text-white/40 text-base text-center py-12">No photos yet.</div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-1" data-testid="photos-grid">
                {allPhotos.map((ph, i) => (
                  <a key={`${ph.postId}-${i}`} href={ph.url} target="_blank" rel="noopener noreferrer" className="block aspect-square overflow-hidden" data-testid={`photo-${i}`}>
                    <img src={ph.url} alt="" className="w-full h-full object-cover hover:opacity-80 transition" />
                  </a>
                ))}
              </div>
            )}
          </section>
        );
      })()}

      {tab === "streams" && (
        <section className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="streams-tab">
          {p.streams.length === 0 && <div className="text-white/40 text-sm col-span-full">No streams yet.</div>}
          {p.streams.map((s) => {
            const canDeleteStream = user && (isMe || user.role === "owner" || user.role === "staff");
            return (
              <div key={s.id} className="group border border-white/10 overflow-hidden relative">
                <Link to={`/watch/${s.id}`} className="block">
                  <div className="aspect-video overflow-hidden relative">
                    <img src={s.thumbnail} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    {s.is_live && s.livekit_room && <LivePreview streamId={s.id} fallback={null} />}
                    {s.is_live && <div className="absolute top-3 left-3 bg-nexus-crimson px-2 py-1 text-[9px] tracking-[0.3em] font-display uppercase animate-pulse">● Live</div>}
                  </div>
                  <div className="p-3">
                    <div className="text-sm font-medium truncate">{s.title}</div>
                    <div className="text-[11px] text-white/40 mt-1">{s.viewers.toLocaleString()} viewers</div>
                  </div>
                </Link>
                {canDeleteStream && (
                  <button
                    onClick={async (e) => {
                      e.preventDefault(); e.stopPropagation();
                      if (!window.confirm(s.is_live ? "This stream is still live. End and delete it?" : "Delete this stream? This cannot be undone.")) return;
                      try {
                        await api.delete(`/streams/${s.id}`);
                        setP((cur) => cur ? { ...cur, streams: cur.streams.filter((x) => x.id !== s.id) } : cur);
                        toast.success("Stream deleted");
                      } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
                    }}
                    className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/70 hover:bg-nexus-crimson flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition"
                    title="Delete stream"
                    aria-label="Delete stream"
                    data-testid={`delete-stream-${s.id}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            );
          })}
        </section>
      )}

      {tab === "groups" && (
        <section data-testid="groups-tab">
          <ProfileGroupsTab userId={p.id} isMe={isMe} />
        </section>
      )}

      {tab === "breels" && (
        <section data-testid="breels-tab">
          <ComingSoon
            title="BReels is loading"
            subtitle="Each creator's AI short-film feed pauses here while BReels v2 is being built as its own flagship project."
            accent="violet"
            icon={Film}
            testId="profile-breels-coming-soon"
          />
        </section>
      )}

      {tab === "merch" && (
        <section data-testid="merch-tab">
          <ComingSoon
            title="Elysium Wear incoming"
            subtitle="Creator-designed drops, limited runs, and profile-pinned storefronts arrive with the next NEXUS wave."
            accent="amber"
            icon={ShoppingBag}
            testId="profile-merch-coming-soon"
          />
        </section>
      )}

      {reportOpen && <ReportModal targetType="user" targetId={p.id} onClose={() => setReportOpen(false)} />}
      {donateOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-6" onClick={() => setDonateOpen(false)}>
          <div className="glass-strong p-6 max-w-md w-full space-y-4" onClick={(e) => e.stopPropagation()} data-testid="donate-modal">
            <h3 className="font-display text-xl">Donate to {p.display_name}</h3>
            <p className="text-xs text-white/40">95% goes directly to the creator. 5% to Nexus.</p>
            <div className="grid grid-cols-4 gap-2">
              {[10, 25, 50, 100].map((v) => (
                <button key={v} onClick={() => setDonateAmt(v)} className={`py-2 text-[11px] tracking-wider border ${donateAmt == v ? "border-nexus-cyan text-nexus-cyan" : "border-white/10"}`}>{v} NX</button>
              ))}
            </div>
            <input type="number" value={donateAmt} onChange={(e) => setDonateAmt(e.target.value)} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm" data-testid="donate-amount" />
            <textarea value={donateMsg} onChange={(e) => setDonateMsg(e.target.value)} placeholder="Message (optional)" className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm h-20" />
            <button onClick={donate} className="btn-nexus w-full justify-center" data-testid="donate-send"><Sparkles className="w-3.5 h-3.5" /> Send {donateAmt} NX</button>
          </div>
        </div>
      )}

      {editOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-6" onClick={() => setEditOpen(false)}>
          <div className="glass-strong p-6 max-w-lg w-full space-y-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="edit-profile-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl">Edit profile</h3>
              <button onClick={() => setEditOpen(false)}><X className="w-4 h-4 text-white/40" /></button>
            </div>
            <div>
              <div className="label-eyebrow mb-1.5">profile banner</div>
              <div
                ref={bannerBoxRef}
                className={`relative h-24 rounded-xl overflow-hidden bg-gradient-to-r from-nexus-cyan/30 via-nexus-magenta/20 to-nexus-gold/30 ${editForm.banner_url ? "cursor-move" : ""}`}
                onPointerDown={startBannerDrag}
                onPointerMove={moveBannerDrag}
                onPointerUp={endBannerDrag}
                onPointerLeave={endBannerDrag}
              >
                {editForm.banner_url && <img src={editForm.banner_url} alt="" draggable={false} className="absolute inset-0 w-full h-full object-cover pointer-events-none" style={{ objectPosition: editForm.banner_position }} />}
                <input ref={bannerFileRef} type="file" accept="image/*" className="hidden" onChange={uploadBanner} />
                <button onClick={() => bannerFileRef.current?.click()} disabled={bannerBusy} className="absolute bottom-2 right-2 btn-ghost text-[10px] bg-black/50" data-testid="banner-upload-btn">
                  <Camera className="w-3 h-3" /> {bannerBusy ? "Uploading…" : editForm.banner_url ? "Change banner" : "Upload banner"}
                </button>
              </div>
              {editForm.banner_url && <p className="text-[10px] text-white/40 mt-1">Drag the image to choose what stays visible.</p>}
            </div>
            <div className="flex items-center gap-4">
              <img src={editForm.avatar || p.avatar} alt="" className="w-20 h-20 ring-2 ring-white/20 object-cover" />
              <div className="space-y-2 flex-1">
                <input ref={avatarFileRef} type="file" accept="image/*" className="hidden" onChange={(e) => pickImage(e, (d) => setEditForm({ ...editForm, avatar: d }))} />
                <button onClick={() => avatarFileRef.current?.click()} className="btn-ghost text-[10px] w-full justify-center" data-testid="avatar-upload-btn"><Camera className="w-3 h-3" /> Upload profile picture</button>
                <input placeholder="Or paste image URL" value={editForm.avatar?.startsWith("data:") ? "(uploaded)" : (editForm.avatar || "")} onChange={(e) => setEditForm({ ...editForm, avatar: e.target.value })} className="w-full bg-transparent border border-white/10 px-3 py-2 text-xs" />
              </div>
            </div>
            <div>
              <div className="label-eyebrow mb-1.5">display name</div>
              <input value={editForm.display_name} onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm" data-testid="edit-display-name" />
            </div>
            <div>
              <div className="label-eyebrow mb-1.5">bio</div>
              <textarea value={editForm.bio} onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm h-24" data-testid="edit-bio" />
            </div>
            <div>
              <div className="label-eyebrow mb-1.5">channel logo (optional)</div>
              <div className="flex items-center gap-3">
                {editForm.logo && <img src={editForm.logo} alt="" className="w-12 h-12 ring-1 ring-white/20 object-cover" />}
                <input ref={logoFileRef} type="file" accept="image/*" className="hidden" onChange={(e) => pickImage(e, (d) => setEditForm({ ...editForm, logo: d }))} />
                <button onClick={() => logoFileRef.current?.click()} className="btn-ghost text-[10px]"><Upload className="w-3 h-3" /> Upload logo</button>
              </div>
            </div>
            <button onClick={saveEdit} className="btn-nexus w-full justify-center" data-testid="save-profile">Save profile</button>
          </div>
        </div>
      )}
      {openReel && <ReelViewer reel={openReel} onClose={() => setOpenReel(null)} onShared={load} />}
    </div>
  );
}



// ---------------------------------------------------------------------------
// Profile → Groups tab · lists public groups the profile user is in.
// ---------------------------------------------------------------------------
function ProfileGroupsTab({ userId, isMe }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/groups/user/${userId}`)
      .then(({ data }) => setItems(data.items || []))
      .finally(() => setLoading(false));
  }, [userId]);
  if (loading) return <div className="text-white/40 text-sm py-6">Loading…</div>;
  if (!items.length) {
    return (
      <div className="text-center py-10">
        <Users className="w-6 h-6 mx-auto text-white/30 mb-3" />
        <div className="text-sm text-white/50">
          {isMe ? "You haven't joined any public groups yet." : "This user isn't in any public groups yet."}
        </div>
        {isMe && (
          <Link to="/groups" className="btn-nexus mt-4 inline-flex" data-testid="groups-tab-cta">
            <Users className="w-3 h-3" /> Discover groups
          </Link>
        )}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map((g) => (
        <Link key={g.id} to={`/group/${g.id}`} className="block bg-nexus-surface border border-white/10 rounded overflow-hidden group hover:border-nexus-cyan/40 transition" data-testid={`profile-group-${g.id}`}>
          <div className="aspect-[3/1] bg-gradient-to-br from-nexus-violet/40 to-nexus-cyan/30 overflow-hidden">
            {g.cover_url && <img src={g.cover_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
          </div>
          <div className="p-3">
            <div className="font-display font-bold text-sm">{g.name}</div>
            <div className="text-[10px] text-white/50 mt-0.5">{g.group_type} · {g.member_count || 0} members · {g.their_role}</div>
          </div>
        </Link>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProfileBlockButton — pill-shaped Block/Unblock toggle on every profile that
// isn't your own. Fires /api/users/{uid}/block|/unblock and reloads the page
// so the newsfeed is immediately filtered of the blocked user's activity.
// ---------------------------------------------------------------------------
function ProfileBlockButton({ targetId, targetName }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/users/${targetId}/block-state`)
      .then(({ data }) => setState(data))
      .catch(() => setState({ i_blocked_them: false, they_blocked_me: false }));
  }, [targetId]);
  const toggle = async () => {
    if (busy || !state) return;
    const willBlock = !state.i_blocked_them;
    if (willBlock && !window.confirm(`Block ${targetName}? Their posts and activity will be hidden from you, and they won't be able to follow or friend-request you until you unblock.`)) return;
    setBusy(true);
    try {
      await api.post(`/users/${targetId}/${willBlock ? "block" : "unblock"}`);
      toast.success(willBlock ? `Blocked ${targetName}` : `Unblocked ${targetName}`);
      if (willBlock) {
        // Blocked profiles 404 — bounce the user back to the feed.
        setTimeout(() => { window.location.href = "/feed"; }, 400);
      } else {
        setState({ ...state, i_blocked_them: false });
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  if (!state) return null;
  const blocked = state.i_blocked_them;
  return (
    <button
      onClick={toggle}
      disabled={busy}
      className={`rounded-full px-3 py-1.5 text-[10px] inline-flex items-center gap-1 ${blocked ? "text-nexus-crimson border border-nexus-crimson/50" : "text-nexus-crimson/70 hover:text-nexus-crimson"}`}
      data-testid="profile-block-btn"
    >
      <Ban className="w-3 h-3" /> {busy ? "…" : blocked ? "Unblock" : "Block"}
    </button>
  );
}

